import React from "react";
import { motion } from "framer-motion";
import { Truck, CalendarCheck, Camera, Send, RefreshCcw } from "lucide-react";
import { WORKFLOW } from "@/lib/site";
import { fadeUp, EASE_OUT_QUART } from "@/lib/motion";

const ICONS = [Truck, CalendarCheck, Camera, Send, RefreshCcw];

/**
 * WorkflowDiagram
 * Horizontal step timeline replacing the old vertical 7-step block.
 * Uses a scroll-drawn connecting line and staggered node reveals.
 */
export default function WorkflowDiagram() {
  return (
    <section
      data-testid="workflow-diagram"
      className="relative overflow-hidden bg-[#050609] text-white"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(79,13,255,0.3),transparent_55%)]"
      />
      <div className="container-lw section-lw relative">
        <div className="mb-14 grid gap-6 md:grid-cols-[1fr_auto] md:items-end md:mb-20">
          <div>
            <div className="eyebrow text-[#0AB5FF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#0AB5FF]" />
              The dealership workflow
            </div>
            <h2 className="mt-5 h-display text-4xl md:text-5xl lg:text-6xl">
              From lot arrival to <br />
              <span className="text-gradient-lw">market ready listing.</span>
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-relaxed text-white/60">
            One recurring operation. Five sequential steps. No hand-offs
            between departments, no chasing photos, no forgotten sold
            units. The dealership standard shows up on cadence.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting line */}
          <motion.div
            aria-hidden
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-150px" }}
            transition={{ duration: 1.4, ease: EASE_OUT_QUART }}
            style={{ transformOrigin: "left center" }}
            className="absolute left-0 right-0 top-[42px] hidden h-px bg-gradient-to-r from-[#0AB5FF]/0 via-[#0AB5FF]/50 to-[#4F0DFF] md:block"
          />

          <ol className="grid gap-8 md:grid-cols-5 md:gap-4">
            {WORKFLOW.map((s, idx) => {
              const Icon = ICONS[idx] || Truck;
              return (
                <motion.li
                  key={s.k}
                  data-testid={`workflow-step-${s.k}`}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-80px" }}
                  custom={idx}
                  className="relative"
                >
                  {/* Node */}
                  <div className="relative z-10 flex items-center md:flex-col md:items-start">
                    <div className="grid h-[84px] w-[84px] shrink-0 place-items-center rounded-full border border-white/15 bg-[#0A0C14] shadow-[0_0_0_6px_rgba(5,6,9,1)]">
                      <div className="grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-[#0AB5FF]/25 to-[#4F0DFF]/50 text-white">
                        <Icon className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="ml-4 md:ml-0 md:mt-6">
                      <div className="font-display text-[10px] font-semibold uppercase tracking-[0.28em] text-white/40">
                        Step {s.k}
                      </div>
                      <div className="mt-1.5 font-display text-lg font-semibold tracking-tight md:text-xl">
                        {s.title}
                      </div>
                    </div>
                  </div>

                  <p className="mt-4 pl-[100px] text-sm leading-relaxed text-white/60 md:pl-0">
                    {s.body}
                  </p>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
